import { PRIVACY_CUSTOMER_DELETION_PATH, PRIVACY_CUSTOMER_EXPORTS_PATH, PRIVACY_CUSTOMER_REQUESTS_PATH, privacyRequestListSchema, privacyRequestViewSchema, privacyRetentionMatrixSchema, STAFF_PRIVACY_MATRIX_PATH, STAFF_PRIVACY_REQUESTS_PATH, staffPrivacyRequestListSchema, staffPrivacyRequestViewSchema, } from "@lafayette/schemas";
import { createMemoryTransport, } from "./memory-transport";
/** The download was refused. `status` is the HTTP status (404 for a package that is gone, expired or used up; 403; 401). */
export class PrivacyDownloadError extends Error {
    status;
    constructor(status) {
        super(`the export download was refused (${status})`);
        this.name = "PrivacyDownloadError";
        this.status = status;
    }
}
async function downloadFile(options, path, signal) {
    const base = options.baseUrl.endsWith("/") ? options.baseUrl : `${options.baseUrl}/`;
    const headers = { accept: "application/json" };
    const token = await options.getAccessToken?.();
    if (token)
        headers.authorization = `Bearer ${token}`;
    const fetcher = options.fetch ?? globalThis.fetch.bind(globalThis);
    const response = await fetcher(new URL(path.replace(/^\//, ""), base), {
        method: "GET",
        headers,
        cache: "no-store",
        ...(signal === undefined ? {} : { signal }),
    });
    if (!response.ok)
        throw new PrivacyDownloadError(response.status);
    const disposition = response.headers.get("content-disposition") ?? "";
    const filename = /filename="([^"]{1,200})"/.exec(disposition)?.[1] ?? "lafayette-export.json";
    return {
        filename,
        content: await response.text(),
        sha256: response.headers.get("x-content-sha256") ?? "",
    };
}
export function createCustomerPrivacyApi(options) {
    const send = createMemoryTransport(options);
    return {
        requestExport: (callOptions) => send({
            method: "POST",
            path: PRIVACY_CUSTOMER_EXPORTS_PATH,
            schema: privacyRequestViewSchema,
            idempotencyKey: callOptions?.idempotencyKey,
            signal: callOptions?.signal,
        }),
        requestDeletion: (callOptions) => send({
            method: "POST",
            path: PRIVACY_CUSTOMER_DELETION_PATH,
            schema: privacyRequestViewSchema,
            idempotencyKey: callOptions?.idempotencyKey,
            signal: callOptions?.signal,
        }),
        listRequests: (callOptions) => send({
            method: "GET",
            path: PRIVACY_CUSTOMER_REQUESTS_PATH,
            schema: privacyRequestListSchema,
            signal: callOptions?.signal,
        }),
        cancelRequest: (requestId, callOptions) => send({
            method: "POST",
            path: `${PRIVACY_CUSTOMER_REQUESTS_PATH}/${encodeURIComponent(requestId)}/cancel`,
            schema: privacyRequestViewSchema,
            idempotencyKey: callOptions?.idempotencyKey,
            signal: callOptions?.signal,
        }),
        downloadExport: (requestId, callOptions) => downloadFile(options, `/customer/privacy/exports/${encodeURIComponent(requestId)}/download`, callOptions?.signal),
    };
}
export function createStaffPrivacyApi(options) {
    const send = createMemoryTransport(options);
    const requestPath = (requestId) => `${STAFF_PRIVACY_REQUESTS_PATH}/${encodeURIComponent(requestId)}`;
    return {
        matrix: (callOptions) => send({
            method: "GET",
            path: STAFF_PRIVACY_MATRIX_PATH,
            schema: privacyRetentionMatrixSchema,
            signal: callOptions?.signal,
        }),
        listRequests: (query, callOptions) => {
            const params = new URLSearchParams();
            if (query?.customerId !== undefined)
                params.set("customerId", query.customerId);
            if (query?.kind !== undefined)
                params.set("kind", query.kind);
            if (query?.state !== undefined)
                params.set("state", query.state);
            if (query?.limit !== undefined)
                params.set("limit", String(query.limit));
            return send({
                method: "GET",
                path: STAFF_PRIVACY_REQUESTS_PATH,
                query: params,
                schema: staffPrivacyRequestListSchema,
                signal: callOptions?.signal,
            });
        },
        getRequest: (requestId, callOptions) => send({
            method: "GET",
            path: requestPath(requestId),
            schema: staffPrivacyRequestViewSchema,
            signal: callOptions?.signal,
        }),
        createRequest: (body, callOptions) => send({
            method: "POST",
            path: STAFF_PRIVACY_REQUESTS_PATH,
            body,
            schema: staffPrivacyRequestViewSchema,
            idempotencyKey: callOptions?.idempotencyKey,
            signal: callOptions?.signal,
        }),
        decide: (requestId, body, callOptions) => send({
            method: "POST",
            path: `${requestPath(requestId)}/decision`,
            body,
            schema: staffPrivacyRequestViewSchema,
            idempotencyKey: callOptions?.idempotencyKey,
            signal: callOptions?.signal,
        }),
        cancelRequest: (requestId, body, callOptions) => send({
            method: "POST",
            path: `${requestPath(requestId)}/cancel`,
            body,
            schema: staffPrivacyRequestViewSchema,
            idempotencyKey: callOptions?.idempotencyKey,
            signal: callOptions?.signal,
        }),
        retryRequest: (requestId, body, callOptions) => send({
            method: "POST",
            path: `${requestPath(requestId)}/retry`,
            body,
            schema: staffPrivacyRequestViewSchema,
            idempotencyKey: callOptions?.idempotencyKey,
            signal: callOptions?.signal,
        }),
        downloadExport: (requestId, callOptions) => downloadFile(options, `${requestPath(requestId)}/export`, callOptions?.signal),
    };
}
